/**
 * S2 (AB#2205) — the signed-in staff member's own data. Each query runs only for
 * a staff session (the endpoints are staff-only); owners never call them.
 */
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { apiService } from '../services/api';
import { useAuthContext } from '../contexts/AuthContext';

function useIsStaffSession(): boolean {
    const { isSalonStaff, accessToken } = useAuthContext();
    return !!isSalonStaff && !!accessToken;
}

/** GET /salon-staff/me — salon, branches, position and role label (SG-17). */
export function useStaffMe() {
    const enabled = useIsStaffSession();
    return useQuery({
        queryKey: queryKeys.staffSelf.me(),
        queryFn: () => apiService.getStaffMe(),
        enabled,
        staleTime: 5 * 60_000,
        retry: 1,
    });
}

/** GET /salon-staff/my-day — today (salon-local) unless a date is given (SG-06). */
export function useStaffMyDay(date?: string) {
    const enabled = useIsStaffSession();
    return useQuery({
        queryKey: queryKeys.appointments.staffMyDay(date),
        queryFn: () => apiService.getStaffMyDay(date),
        enabled,
        staleTime: 30_000,
        // New / changed bookings arrive while the page stays open.
        refetchInterval: 60_000,
        retry: 1,
    });
}

/** GET /salon-staff/my-schedule — weekly hours, approved time off, closures (SG-07/08). */
export function useStaffMySchedule(range?: { from?: string; to?: string }) {
    const enabled = useIsStaffSession();
    return useQuery({
        queryKey: queryKeys.staffSelf.schedule(range),
        queryFn: () => apiService.getStaffMySchedule(range),
        enabled,
        staleTime: 5 * 60_000,
        retry: 1,
    });
}
